import React, { useCallback, useEffect, useState } from 'react';
import { FinancialPage }  from './pages/FinancialPage.tsx';
import { CryptoPage }     from './pages/CryptoPage.tsx';
import { IoTPage }        from './pages/IoTPage.tsx';
import { EcommercePage }  from './pages/EcommercePage.tsx';
import { CanvasPage }     from './pages/CanvasPage.tsx';
import { AnalystPage }    from './pages/AnalystPage.tsx';
import { ThemeToggle }    from './components/ThemeToggle.tsx';
import { ErrorBoundary }  from './components/ErrorBoundary.tsx';
import seoRoutes from '../seo-routes.json';

type Page = 'analyst' | 'financial' | 'crypto' | 'iot' | 'ecommerce' | 'canvas';

interface RouteMeta {
  path: string;
  page: Page;
  nav: string;
  icon: string;
  title: string;
  description: string;
}

const ROUTES = seoRoutes as RouteMeta[];
const NAV = ROUTES.map((r) => ({ id: r.page, label: r.nav, icon: r.icon, path: r.path }));

const SITE_ORIGIN = 'https://www.tekivex.com/dataflow';

// ── path <-> page helpers ────────────────────────────────────────────────────
function pageFromPath(pathname: string): Page {
  // Normalize trailing slash; match against the route table
  const clean = pathname.replace(/\/+$/, '') || '/';
  const hit = ROUTES.find((r) => r.path === clean);
  return hit ? hit.page : 'analyst';
}
function routeForPage(page: Page): RouteMeta {
  return ROUTES.find((r) => r.page === page) ?? ROUTES[0]!;
}

// ── per-route document head (client-side; crawlers get it from prerendered HTML)
function applyHead(page: Page): void {
  if (typeof document === 'undefined') return;
  const r = routeForPage(page);
  document.title = r.title;
  setMeta('name', 'description', r.description);
  setLink('canonical', SITE_ORIGIN + (r.path === '/' ? '/' : r.path));
  setMeta('property', 'og:title', r.title);
  setMeta('property', 'og:description', r.description);
  setMeta('property', 'og:url', SITE_ORIGIN + (r.path === '/' ? '/' : r.path));
}
function setMeta(attr: 'name' | 'property', key: string, value: string): void {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!el) { el = document.createElement('meta'); el.setAttribute(attr, key); document.head.appendChild(el); }
  el.setAttribute('content', value);
}
function setLink(rel: string, href: string): void {
  let el = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!el) { el = document.createElement('link'); el.setAttribute('rel', rel); document.head.appendChild(el); }
  el.setAttribute('href', href);
}

export default function App() {
  const [page, setPage] = useState<Page>(() =>
    typeof window === 'undefined' ? 'analyst' : pageFromPath(window.location.pathname),
  );

  // Navigate: push a real URL + update view (no full reload)
  const navigate = useCallback((next: Page) => {
    setPage(next);
    const r = routeForPage(next);
    if (typeof window !== 'undefined' && window.location.pathname !== r.path) {
      window.history.pushState({ page: next }, '', r.path);
    }
    applyHead(next);
    // GA4 virtual pageview on SPA navigation (replaces the dead hashchange handler)
    const g = (window as unknown as { gtag?: (...a: unknown[]) => void }).gtag;
    if (g) g('event', 'page_view', { page_path: r.path });
  }, []);

  // Back/forward button support
  useEffect(() => {
    const onPop = () => { const p = pageFromPath(window.location.pathname); setPage(p); applyHead(p); };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  // Apply head for the initial route on mount
  useEffect(() => { applyHead(page); /* eslint-disable-next-line */ }, []);

  return (
    <div className="app">
      {/* Top Bar */}
      <header className="topbar">
        <div className="topbar-brand">
          <a href="/" onClick={(e) => { e.preventDefault(); navigate('analyst'); }} style={{ display: 'flex', alignItems: 'center', gap: 8, textDecoration: 'none', color: 'inherit' }}>
            <svg width="28" height="28" viewBox="0 0 32 32" fill="none">
              <rect width="32" height="32" rx="8" fill="#6366f1"/>
              <path d="M8 22 L14 10 L20 18 L26 10" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
              <circle cx="8" cy="22" r="2" fill="#10b981"/>
              <circle cx="26" cy="10" r="2" fill="#f59e0b"/>
            </svg>
            <span className="brand-name">DataFlow</span>
          </a>
          <span className="brand-version">v0.3.3</span>
        </div>

        <nav className="topbar-nav">
          {NAV.map((n) => (
            <a
              key={n.id}
              href={n.path}
              className={`nav-btn ${page === n.id ? 'nav-btn--active' : ''}`}
              onClick={(e) => { e.preventDefault(); navigate(n.id as Page); }}
            >
              <span>{n.icon}</span>
              {n.label}
            </a>
          ))}
        </nav>

        <div className="topbar-links">
          <ThemeToggle />
          <a href="https://github.com/007krcs/dataflow" target="_blank" rel="noreferrer" className="gh-link">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 0C5.37 0 0 5.37 0 12c0 5.31 3.435 9.795 8.205 11.385.6.105.825-.255.825-.57 0-.285-.015-1.23-.015-2.235-3.015.555-3.795-.735-4.035-1.41-.135-.345-.72-1.41-1.23-1.695-.42-.225-1.02-.78-.015-.795.945-.015 1.62.87 1.845 1.23 1.08 1.815 2.805 1.305 3.495.99.105-.78.42-1.305.765-1.605-2.67-.3-5.46-1.335-5.46-5.925 0-1.305.465-2.385 1.23-3.225-.12-.3-.54-1.53.12-3.18 0 0 1.005-.315 3.3 1.23.96-.27 1.98-.405 3-.405s2.04.135 3 .405c2.295-1.56 3.3-1.23 3.3-1.23.66 1.65.24 2.88.12 3.18.765.84 1.23 1.905 1.23 3.225 0 4.605-2.805 5.625-5.475 5.925.435.375.81 1.095.81 2.22 0 1.605-.015 2.895-.015 3.3 0 .315.225.69.825.57A12.02 12.02 0 0 0 24 12c0-6.63-5.37-12-12-12z"/>
            </svg>
            GitHub
          </a>
        </div>
      </header>

      {/* Page Content — wrapped so a single view crash can't blank the whole app.
          `key={page}` resets the boundary on navigation so a crashed view can recover. */}
      <main className="main-content">
        <ErrorBoundary key={page}>
          {page === 'analyst'    && <AnalystPage />}
          {page === 'financial'  && <FinancialPage />}
          {page === 'crypto'     && <CryptoPage />}
          {page === 'iot'        && <IoTPage />}
          {page === 'ecommerce'  && <EcommercePage />}
          {page === 'canvas'     && <CanvasPage />}
        </ErrorBoundary>
      </main>
    </div>
  );
}
